import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { resolveMediaUrl } from '@/components/CatalogMediaUploader';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { 
  ShoppingCart, Plus, Minus, Trash2, Phone, MapPin, Send, 
  Instagram, Facebook, Store, X, Check, Loader2, Package
} from 'lucide-react';

export const CatalogoPage = () => {
  const { slug } = useParams();
  const [catalog, setCatalog] = useState(null);
  const primary = catalog?.company?.primary_color || '#059669';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  
  const [orderForm, setOrderForm] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    delivery_type: 'retirada',
    delivery_address: '',
    payment_method: 'pix',
    notes: ''
  });

  useEffect(() => {
    loadCatalog();
  }, [slug]);

  const loadCatalog = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/public/catalogo/${slug}`);
      setCatalog(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Catálogo não encontrado');
    } finally {
      setLoading(false);
    }
  };

  // Carrinho
  const addToCart = (product) => {
    const existing = cart.find(item => item.product.id === product.id);
    if (existing) {
      setCart(cart.map(item => 
        item.product.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { product, quantity: 1 }]);
    }
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product.id === productId) {
        const newQty = Math.max(0, item.quantity + delta);
        return newQty === 0 ? null : { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product.id !== productId));
  };

  const getCartTotal = () => {
    const subtotal = cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
    const deliveryFee = orderForm.delivery_type === 'entrega' && catalog?.delivery?.enabled 
      ? catalog.delivery.fee 
      : 0;
    return { subtotal, deliveryFee, total: subtotal + deliveryFee };
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Filtrar produtos
  const getFilteredProducts = () => {
    if (!catalog) return [];
    let products = catalog.products || [];
    
    if (selectedCategory !== 'all') {
      products = products.filter(p => p.category === selectedCategory);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      products = products.filter(p => 
        p.name.toLowerCase().includes(term) ||
        p.description?.toLowerCase().includes(term)
      );
    }
    
    return products;
  };

  // Enviar pedido
  const handleSubmitOrder = async () => {
    if (!orderForm.customer_name || !orderForm.customer_phone) {
      alert('Preencha nome e telefone');
      return;
    }
    
    if (orderForm.delivery_type === 'entrega' && !orderForm.delivery_address) {
      alert('Preencha o endereço de entrega');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.post(`/public/catalogo/${slug}/pedido`, {
        ...orderForm,
        items: cart.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity
        }))
      });
      
      setOrderSuccess(response.data);
      setCart([]);
      setShowCheckout(false);
      setShowCart(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao enviar pedido');
    } finally {
      setSubmitting(false);
    }
  };

  // Enviar via WhatsApp
  const sendWhatsApp = () => {
    if (!catalog?.company?.whatsapp) return;
    
    const { subtotal, deliveryFee, total } = getCartTotal();
    let message = `🛒 *Pedido via Catálogo*\n\n`;
    message += `*Cliente:* ${orderForm.customer_name}\n`;
    message += `*Telefone:* ${orderForm.customer_phone}\n`;
    
    if (orderForm.delivery_type === 'entrega') {
      message += `*Entrega:* ${orderForm.delivery_address}\n`;
    } else {
      message += `*Retirada no local*\n`;
    }
    
    message += `\n*Itens:*\n`;
    cart.forEach(item => {
      message += `• ${item.quantity}x ${item.product.name} - ${formatCurrency(item.product.price * item.quantity)}\n`;
    });
    
    message += `\n*Subtotal:* ${formatCurrency(subtotal)}`;
    if (deliveryFee > 0) {
      message += `\n*Taxa de entrega:* ${formatCurrency(deliveryFee)}`;
    }
    message += `\n*Total:* ${formatCurrency(total)}`;
    message += `\n\n*Pagamento:* ${orderForm.payment_method.toUpperCase()}`;
    
    if (orderForm.notes) {
      message += `\n\n*Obs:* ${orderForm.notes}`;
    }
    
    const phone = catalog.company.whatsapp.replace(/\D/g, '');
    window.open(`https://wa.me/55${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <Store className="w-16 h-16 text-slate-300 mb-4" />
        <h1 className="text-2xl font-bold text-slate-700 mb-2">Catálogo não encontrado</h1>
        <p className="text-slate-500">{error}</p>
      </div>
    );
  }

  if (orderSuccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ backgroundColor: `${primary}14` }}>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${primary}26` }}>
            <Check className="w-8 h-8" style={{ color: primary }} />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Pedido Enviado!</h1>
          <p className="text-slate-600 mb-4">
            Seu pedido <strong>{orderSuccess.order?.order_number}</strong> foi recebido com sucesso!
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Em breve entraremos em contato para confirmar.
          </p>
          <Button onClick={() => { setOrderSuccess(null); loadCatalog(); }} className="w-full">
            Fazer Novo Pedido
          </Button>
        </div>
      </div>
    );
  }

  const { subtotal, deliveryFee, total } = getCartTotal();
  const categories = Object.keys(catalog?.categories || {});

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        {catalog?.company?.banner && (
          <div 
            className="h-36 sm:h-48 bg-cover bg-center relative"
            style={{ backgroundImage: `url(${resolveMediaUrl(catalog.company.banner)})` }}
            data-testid="catalog-banner"
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>
        )}
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className={`flex items-center gap-4 ${catalog?.company?.banner ? '-mt-12' : ''}`}>
            {catalog?.company?.logo ? (
              <img 
                src={resolveMediaUrl(catalog.company.logo)} 
                alt={catalog.company.name}
                className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg bg-white"
                data-testid="catalog-logo"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-slate-200 border-4 border-white shadow flex items-center justify-center">
                <Store className="w-8 h-8 text-slate-400" />
              </div>
            )}
            <div className={`flex-1 ${catalog?.company?.banner ? 'mt-10' : ''}`}>
              <h1 className="text-xl font-bold text-slate-800">{catalog?.company?.name}</h1>
              {catalog?.company?.description && (
                <p className="text-sm text-slate-500 line-clamp-2">{catalog.company.description}</p>
              )}
            </div>
          </div>
          
          {/* Contato */}
          <div className="flex flex-wrap gap-3 mt-3 text-sm">
            {catalog?.company?.phone && (
              <a href={`tel:${catalog.company.phone}`} className="flex items-center gap-1 text-slate-600 hover:opacity-80">
                <Phone className="w-4 h-4" />
                {catalog.company.phone}
              </a>
            )}
            {catalog?.company?.address && (
              <span className="flex items-center gap-1 text-slate-600">
                <MapPin className="w-4 h-4" />
                {catalog.company.address}
              </span>
            )}
            {catalog?.company?.instagram && (
              <a href={`https://instagram.com/${catalog.company.instagram}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-slate-600 hover:text-pink-600">
                <Instagram className="w-4 h-4" />
                @{catalog.company.instagram}
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Busca e Filtros */}
      <div className="max-w-4xl mx-auto px-4 py-4 sticky top-[140px] z-30 bg-slate-50">
        <Input
          placeholder="Buscar produtos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="mb-3"
        />
        <ScrollArea className="w-full whitespace-nowrap">
          <div className="flex gap-2 pb-2">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory('all')}
              className={selectedCategory === 'all' ? 'bg-slate-900' : ''}
            >
              Todos
            </Button>
            {categories.map(cat => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat)}
                className={selectedCategory === cat ? 'bg-slate-900' : ''}
              >
                {cat}
              </Button>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Produtos */}
      <main className="max-w-4xl mx-auto px-4 pb-24">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {getFilteredProducts().map(product => {
            const inCart = cart.find(item => item.product.id === product.id);
            return (
              <Card key={product.id} className="overflow-hidden">
                {product.image ? (
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-32 object-cover"
                  />
                ) : (
                  <div className="w-full h-32 bg-slate-100 flex items-center justify-center">
                    <Package className="w-12 h-12 text-slate-300" />
                  </div>
                )}
                <CardContent className="p-3">
                  <h3 className="font-semibold text-sm line-clamp-2 mb-1">{product.name}</h3>
                  <p className="text-lg font-bold mb-2" style={{ color: primary }}>
                    {formatCurrency(product.price)}
                  </p>
                  {inCart ? (
                    <div className="flex items-center justify-between">
                      <Button size="sm" variant="outline" onClick={() => updateQuantity(product.id, -1)}>
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="font-semibold">{inCart.quantity}</span>
                      <Button size="sm" variant="outline" onClick={() => updateQuantity(product.id, 1)}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" className="w-full text-white hover:brightness-110" style={{ backgroundColor: primary }} onClick={() => addToCart(product)} data-testid={`catalog-add-${product.id}`}>
                      <Plus className="w-4 h-4 mr-1" /> Adicionar
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {getFilteredProducts().length === 0 && (
          <div className="text-center py-12 text-slate-500">
            Nenhum produto encontrado
          </div>
        )}
      </main>

      {/* Botão do Carrinho Flutuante */}
      {cartCount > 0 && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-4 right-4 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2 hover:brightness-110 transition-all z-50" style={{ backgroundColor: primary }} data-testid="catalog-cart-btn"
        >
          <ShoppingCart className="w-5 h-5" />
          <span className="font-semibold">{cartCount} itens</span>
          <span className="opacity-60">•</span>
          <span className="font-bold">{formatCurrency(subtotal)}</span>
        </button>
      )}

      {/* Modal do Carrinho */}
      {showCart && (
        <div className="fixed inset-0 z-50 bg-black/50" onClick={() => setShowCart(false)}>
          <div 
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[85vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-bold">Seu Carrinho</h2>
              <button onClick={() => setShowCart(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <ScrollArea className="max-h-[50vh]">
              <div className="p-4 space-y-3">
                {cart.map(item => (
                  <div key={item.product.id} className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg">
                    {item.product.image ? (
                      <img src={item.product.image} className="w-16 h-16 rounded object-cover" alt="" />
                    ) : (
                      <div className="w-16 h-16 rounded bg-slate-200 flex items-center justify-center">
                        <Package className="w-6 h-6 text-slate-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-semibold text-sm">{item.product.name}</p>
                      <p className="font-bold" style={{ color: primary }}>{formatCurrency(item.product.price * item.quantity)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => updateQuantity(item.product.id, -1)}>
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-6 text-center font-semibold">{item.quantity}</span>
                      <Button size="sm" variant="outline" onClick={() => updateQuantity(item.product.id, 1)}>
                        <Plus className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => removeFromCart(item.product.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
            
            <div className="p-4 border-t bg-white">
              <div className="flex justify-between mb-2">
                <span>Subtotal</span>
                <span className="font-semibold">{formatCurrency(subtotal)}</span>
              </div>
              <Button 
                className="w-full text-white hover:brightness-110" style={{ backgroundColor: primary }}
                size="lg"
                onClick={() => { setShowCart(false); setShowCheckout(true); }}
              >
                Finalizar Pedido
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Checkout */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 bg-black/50" onClick={() => setShowCheckout(false)}>
          <div 
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[90vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-bold">Finalizar Pedido</h2>
              <button onClick={() => setShowCheckout(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <ScrollArea className="max-h-[60vh]">
              <div className="p-4 space-y-4">
                <div>
                  <Label>Nome *</Label>
                  <Input
                    value={orderForm.customer_name}
                    onChange={e => setOrderForm({...orderForm, customer_name: e.target.value})}
                    placeholder="Seu nome"
                  />
                </div>
                
                <div>
                  <Label>Telefone/WhatsApp *</Label>
                  <Input
                    value={orderForm.customer_phone}
                    onChange={e => setOrderForm({...orderForm, customer_phone: e.target.value})}
                    placeholder="(00) 00000-0000"
                  />
                </div>
                
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={orderForm.customer_email}
                    onChange={e => setOrderForm({...orderForm, customer_email: e.target.value})}
                    placeholder="seu@email.com"
                  />
                </div>
                
                <div>
                  <Label>Tipo de Entrega</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <Button
                      type="button"
                      variant={orderForm.delivery_type === 'retirada' ? 'default' : 'outline'}
                      onClick={() => setOrderForm({...orderForm, delivery_type: 'retirada'})}
                      className={orderForm.delivery_type === 'retirada' ? 'bg-slate-900' : ''}
                    >
                      Retirar no Local
                    </Button>
                    {catalog?.delivery?.enabled && (
                      <Button
                        type="button"
                        variant={orderForm.delivery_type === 'entrega' ? 'default' : 'outline'}
                        onClick={() => setOrderForm({...orderForm, delivery_type: 'entrega'})}
                        className={orderForm.delivery_type === 'entrega' ? 'bg-slate-900' : ''}
                      >
                        Entrega (+{formatCurrency(catalog.delivery.fee)})
                      </Button>
                    )}
                  </div>
                </div>
                
                {orderForm.delivery_type === 'entrega' && (
                  <div>
                    <Label>Endereço de Entrega *</Label>
                    <Textarea
                      value={orderForm.delivery_address}
                      onChange={e => setOrderForm({...orderForm, delivery_address: e.target.value})}
                      placeholder="Rua, número, bairro, complemento..."
                    />
                  </div>
                )}
                
                <div>
                  <Label>Forma de Pagamento</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {['pix', 'dinheiro', 'cartao'].map(method => (
                      <Button
                        key={method}
                        type="button"
                        variant={orderForm.payment_method === method ? 'default' : 'outline'}
                        onClick={() => setOrderForm({...orderForm, payment_method: method})}
                        className={orderForm.payment_method === method ? 'bg-slate-900' : ''}
                        size="sm"
                      >
                        {method.charAt(0).toUpperCase() + method.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label>Observações</Label>
                  <Textarea
                    value={orderForm.notes}
                    onChange={e => setOrderForm({...orderForm, notes: e.target.value})}
                    placeholder="Alguma observação sobre o pedido?"
                  />
                </div>
              </div>
            </ScrollArea>
            
            <div className="p-4 border-t bg-white space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              {orderForm.delivery_type === 'entrega' && catalog?.delivery?.enabled && (
                <div className="flex justify-between text-sm">
                  <span>Taxa de entrega</span>
                  <span>{formatCurrency(catalog.delivery.fee)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total</span>
                <span style={{ color: primary }}>{formatCurrency(total)}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-2 pt-2">
                {catalog?.company?.whatsapp && (
                  <Button 
                    variant="outline" 
                    style={{ borderColor: primary, color: primary }}
                    onClick={sendWhatsApp}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                )}
                <Button 
                  className={`text-white hover:brightness-110 ${!catalog?.company?.whatsapp ? 'col-span-2' : ''}`} style={{ backgroundColor: primary }}
                  onClick={handleSubmitOrder}
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Enviar Pedido
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatalogoPage;
